interface SSEEvent {
  data?: string;
  id?: string;
  retry?: number;
}

class TextLineStream extends TransformStream<string, string> {
  #currentLine = "";

  constructor() {
    super({
      transform: (chars, controller) => {
        const text = this.#currentLine + chars;
        let start = 0;
        let lfIndex = text.indexOf("\n");
        let crIndex = text.indexOf("\r");
        for (;;) {
          // Exclude \r\n CRLF sequences — the \n branch handles them
          if (crIndex >= 0 && crIndex !== text.length - 1 && (lfIndex === -1 || lfIndex - 1 > crIndex)) {
            controller.enqueue(text.slice(start, crIndex));
            start = crIndex + 1;
            crIndex = text.indexOf("\r", start);
            continue;
          }

          if (lfIndex === -1) break;

          controller.enqueue(text.slice(start, text[lfIndex - 1] === "\r" ? lfIndex - 1 : lfIndex));
          start = lfIndex + 1;
          lfIndex = text.indexOf("\n", start);
          if (crIndex >= 0 && crIndex < start) crIndex = text.indexOf("\r", start);
        }
        this.#currentLine = text.slice(start);
      },
      flush: (controller) => {
        if (this.#currentLine === "") return;
        const currentLine = this.#currentLine.endsWith("\r") ? this.#currentLine.slice(0, -1) : this.#currentLine;
        controller.enqueue(currentLine);
      },
    });
  }
}

function toLineStream(body: ReadableStream<Uint8Array>): ReadableStream<string> {
  // TextDecoderStream types writable as WritableStream<BufferSource> but
  // pipeThrough expects WritableStream<Uint8Array>. Safe since Uint8Array
  // satisfies BufferSource at runtime.
  return body
    .pipeThrough(new TextDecoderStream() as unknown as TransformStream<Uint8Array, string>)
    .pipeThrough(new TextLineStream());
}

function splitField(input: string): [string, string] | undefined {
  const colonIndex = input.indexOf(":");
  if (colonIndex === 0) {
    return undefined;
  }
  if (colonIndex === -1) {
    return [input, ""];
  }
  const value = input[colonIndex + 1] === " " ? input.substring(colonIndex + 2) : input.substring(colonIndex + 1);
  return [input.substring(0, colonIndex), value];
}

async function* events(response: Response, signal?: AbortSignal): AsyncGenerator<SSEEvent, void, undefined> {
  if (!response.body) return;
  if (signal?.aborted) return;

  const reader = toLineStream(response.body).getReader();
  // Once the aborted fetch has errored the body, cancel rejects with the same AbortError.
  const onAbort = () => void reader.cancel().catch(() => {});
  signal?.addEventListener("abort", onAbort, { once: true });
  let event: SSEEvent | undefined;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        if (event && !signal?.aborted) yield event;
        return;
      }

      if (value === "") {
        if (event) yield event;
        event = undefined;
        continue;
      }

      const parts = splitField(value);
      if (!parts) continue;

      const [field, fieldValue] = parts;

      event ??= {};
      switch (field) {
        case "data":
          event.data = event.data ? `${event.data}\n${fieldValue}` : fieldValue;
          break;
        case "id":
          event.id = fieldValue;
          break;
        case "retry": {
          const parsed = Number(fieldValue);
          if (!Number.isNaN(parsed) && /^\d+$/.test(fieldValue)) {
            event.retry = parsed;
          }
          break;
        }
      }
    }
  } finally {
    signal?.removeEventListener("abort", onAbort);
    reader.releaseLock();
  }
}

// The schedule every event-stream subscription in this package reconnects on.
// The base delay is whatever the server last asked for through the `retry`
// field, doubled once per consecutive failure and capped; the jitter keeps a
// fleet of tabs coming back from one outage from reconnecting in lockstep.
const DEFAULT_RECONNECT_DELAY_MS = 2_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const RECONNECT_JITTER_MS = 1_000;
const MAX_BACKOFF_DOUBLINGS = 5;
// A connection that stays up this long counts as healthy, so the next drop
// starts the schedule over at the base delay instead of where it left off. A
// server short of capacity answers 200 and then closes, so a response alone
// proves nothing.
const STABILITY_WINDOW_MS = 5_000;

interface SSESubscription {
  close: () => void;
}

interface SSESubscriptionOptions {
  /** Absolute URL of the event-stream endpoint. */
  url: string;
  /**
   * Headers for one attempt, built per (re)connect so rotated auth is picked
   * up. A throw fails that attempt into the normal reconnect path.
   */
  buildHeaders?: () => Record<string, string>;
  /**
   * How many reconnects to make before giving up and reporting through
   * onError. A subscription that gave up starts over, with the full budget,
   * when the page comes back online or into view. Omit to keep retrying for as
   * long as the subscription is open, which is what a stream a person is
   * watching live wants.
   *
   * Either way a 4xx other than 408 and 429 is never retried: it is reported
   * once, and the subscription stays down until buildHeaders returns a
   * different Authorization, checked locally at the capped delay and whenever
   * the page comes back online or into view.
   */
  maxAttempts?: number;
  /** One frame's data payload, already joined across multi-line data fields. */
  onData: (data: string) => void;
  onError?: (error: Error) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  /**
   * Fired once per outage, when a connection after at least one failed or
   * dropped attempt proves healthy: it delivers a frame with data, or stays
   * open for the stability window. A connection that closes before either
   * counts as another failure.
   */
  onReconnect?: () => void;
}

// Connect, tail, and reconnect on the schedule above, resuming from the last
// frame id the server sent. Streams whose transport carries no ids simply never
// set one and so never send the header.
export function subscribeToSSE(options: SSESubscriptionOptions): SSESubscription {
  let abortController: AbortController | null = new AbortController();
  let isClosing = false;
  let reconnectAttempts = 0;
  let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let stabilityTimerId: ReturnType<typeof setTimeout> | null = null;
  let serverRetryMs = DEFAULT_RECONNECT_DELAY_MS;
  let lastEventId: string | null = null;
  let droppedSinceHealthy = false;
  let gaveUp = false;
  // Set by a non-retryable 4xx, holding the credentials it was refused for.
  let rejected: { authorization: string | undefined } | null = null;

  const reconnectDelay = () => {
    const exponential = serverRetryMs * 2 ** Math.min(reconnectAttempts, MAX_BACKOFF_DOUBLINGS);
    const jitter = Math.random() * RECONNECT_JITTER_MS;
    return Math.min(exponential, MAX_RECONNECT_DELAY_MS) + jitter;
  };

  const clearStabilityTimer = () => {
    if (stabilityTimerId) {
      clearTimeout(stabilityTimerId);
      stabilityTimerId = null;
    }
  };

  const markHealthy = () => {
    if (!droppedSinceHealthy) return;
    droppedSinceHealthy = false;
    options.onReconnect?.();
  };

  const scheduleReconnect = () => {
    if (isClosing) return;

    if (options.maxAttempts !== undefined && reconnectAttempts >= options.maxAttempts) {
      gaveUp = true;
      options.onError?.(new Error("Failed to connect after multiple attempts"));
      return;
    }

    const delay = reconnectDelay();
    reconnectAttempts++;

    reconnectTimeoutId = setTimeout(() => {
      reconnectTimeoutId = null;
      if (isClosing) return;
      abortController = new AbortController();
      void connect();
    }, delay);
  };

  const restart = () => {
    if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
    gaveUp = false;
    rejected = null;
    reconnectAttempts = 0;
    abortController = new AbortController();
    void connect();
  };

  // buildHeaders throws while the caller's auth or scope isn't ready, which
  // can't have produced different credentials yet.
  const credentialsChanged = () => {
    try {
      return options.buildHeaders?.().Authorization !== rejected?.authorization;
    } catch {
      return false;
    }
  };

  // Reading the credentials is local, so a token refreshed in a tab that never
  // loses focus is picked up within the capped delay with no request between.
  const watchCredentials = () => {
    reconnectTimeoutId = setTimeout(
      () => {
        reconnectTimeoutId = null;
        if (isClosing) return;
        if (credentialsChanged()) restart();
        else watchCredentials();
      },
      MAX_RECONNECT_DELAY_MS + Math.random() * RECONNECT_JITTER_MS
    );
  };

  const connect = async () => {
    if (isClosing || !abortController) return;

    try {
      const headers: Record<string, string> = {
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
        ...options.buildHeaders?.(),
      };
      if (lastEventId) headers["Last-Event-ID"] = lastEventId;

      const response = await fetch(options.url, {
        method: "GET",
        headers,
        signal: abortController.signal,
        credentials: "include",
      });

      if (!response.ok) {
        const { status } = response;
        // Any other 4xx answers the same way for the same URL and credentials,
        // so retrying it only multiplies the request count by the open tabs.
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
          rejected = { authorization: headers.Authorization };
          options.onError?.(
            new Error(
              status === 401 || status === 403
                ? `Authentication error: ${status}`
                : `HTTP error: ${status} ${response.statusText}`
            )
          );
          return;
        }
        throw new Error(`HTTP error: ${status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      options.onConnect?.();

      clearStabilityTimer();
      stabilityTimerId = setTimeout(() => {
        reconnectAttempts = 0;
        stabilityTimerId = null;
        markHealthy();
      }, STABILITY_WINDOW_MS);

      for await (const frame of events(response, abortController.signal)) {
        if (frame.retry != null) serverRetryMs = frame.retry;
        if (frame.id) lastEventId = frame.id;
        if (!frame.data) continue;
        // The backoff still waits out the stability window: a server that
        // sends one frame and then drops must not be retried at the base delay.
        markHealthy();
        options.onData(frame.data);
      }
    } catch (error) {
      if (!isClosing && error instanceof Error && error.name !== "AbortError") {
        options.onError?.(error);
      }
    } finally {
      clearStabilityTimer();
      if (!isClosing) {
        droppedSinceHealthy = true;
        options.onDisconnect?.();
        if (rejected) watchCredentials();
        else scheduleReconnect();
      }
    }
  };

  // A subscription that gave up waits for a sign the network or the user is
  // back rather than for a reload. A tab still hidden stays down until it is
  // shown, so background tabs don't all return at once. One the server
  // rejected also needs different credentials, or every tab switch would replay
  // the same rejection.
  const resume = () => {
    if (isClosing || (!gaveUp && !rejected)) return;
    if (document.visibilityState === "hidden") return;
    if (rejected && !credentialsChanged()) return;
    restart();
  };
  if (typeof window !== "undefined") window.addEventListener("online", resume);
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", resume);

  void connect();

  return {
    close: () => {
      if (isClosing) return;
      isClosing = true;
      if (typeof window !== "undefined") window.removeEventListener("online", resume);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", resume);
      clearStabilityTimer();
      if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
      abortController?.abort();
      abortController = null;
      options.onDisconnect?.();
    },
  };
}
